import React, { useMemo, useState, createElement, Component } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/Tabs';
import { Badge } from '../components/Badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/Table';
import { Database, Search, Plus, Download, Edit2, Trash2, DatabaseZap } from 'lucide-react';
import { BINDA_LIST, KATEGORI_LAPORAN, SEMUA_STATUS, STATUS_META, statusColor } from '../data/binda';
import { reportTypesList, alertTypesList, kpiSettingsList, userRolesList, systemParamsList } from '../data/mockData';
import { useNavigate } from 'react-router-dom';
import { StatusBadge } from '../components/StatusBadge';
import { UrgencyBadge } from '../components/UrgencyBadge';
import { Button } from '../components/Button';
import { EmptyState } from '../components/EmptyState';
import { toast } from 'sonner';
import { SyncButton } from '../components/SyncButton';
import { FilterBar, FilterField } from '../components/FilterBar';
import { Filter } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/Dialog';
// --- Helper for CSV Export ---
const downloadCSV = (data: any[], filename: string, columns: {
  key: string;
  label: string;
}[]) => {
  if (data.length === 0) {
    toast.error('Tidak ada data untuk diekspor.');
    return;
  }
  const headers = columns.map(c => c.label);
  const escape = (val: any) => `"${String(val || '').replace(/"/g, '""')}"`;
  const rows = data.map(item => columns.map(c => escape(item[c.key])).join(','));
  const csv = [headers.map(escape).join(','), ...rows].join('\n');
  const blob = new Blob([csv], {
    type: 'text/csv;charset=utf-8;'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  toast.success(`${data.length} baris diekspor ke CSV.`);
};
// --- Generic CRUD Section Component ---
interface ColumnDef {
  key: string;
  label: string;
  render?: (val: any, item: any) => React.ReactNode;
}
interface FormFieldDef {
  key: string;
  label: string;
  type: 'text' | 'number' | 'select';
  options?: string[];
}
interface CrudSectionProps {
  title: string;
  initialData: any[];
  columns: ColumnDef[];
  searchKeys: string[];
  formFields: FormFieldDef[];
  idKey?: string;
  onRowClick?: (item: any) => void;
}
function CrudSection({
  title,
  initialData,
  columns,
  searchKeys,
  formFields,
  idKey = 'id',
  onRowClick
}: CrudSectionProps) {
  const [data, setData] = useState(initialData);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [deletingItem, setDeletingItem] = useState<any>(null);
  const [formData, setFormData] = useState<any>({});
  const [showFilters, setShowFilters] = useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});
  const handleFilterChange = (key: string, value: any) => {
    setFilterValues(prev => ({
      ...prev,
      [key]: value
    }));
  };
  const handleResetFilters = () => {
    setFilterValues({});
  };
  const filteredData = useMemo(() => {
    let result = data;
    const search = filterValues.search?.toLowerCase() || '';
    if (search) {
      result = result.filter(item => searchKeys.some(key => String(item[key] || '').toLowerCase().includes(search)));
    }
    return result;
  }, [data, filterValues.search, searchKeys]);
  const filterFields: FilterField[] = [{
    key: 'search',
    label: 'Pencarian',
    type: 'search',
    placeholder: `Cari ${title.toLowerCase()}...`
  }];
  const handleAddClick = () => {
    setEditingItem(null);
    const emptyForm: any = {};
    formFields.forEach(f => emptyForm[f.key] = f.type === 'number' ? 0 : f.options ? f.options[0] : '');
    setFormData(emptyForm);
    setIsModalOpen(true);
  };
  const handleEditClick = (item: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingItem(item);
    setFormData({
      ...item
    });
    setIsModalOpen(true);
  };
  const handleDeleteClick = (item: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeletingItem(item);
    setIsDeleteOpen(true);
  };
  const handleSave = () => {
    if (editingItem) {
      setData(data.map(item => item[idKey] === editingItem[idKey] ? {
        ...item,
        ...formData
      } : item));
      toast.success('Perubahan disimpan');
    } else {
      const newItem = {
        ...formData,
        [idKey]: formData[idKey] || `NEW-${Date.now()}`
      };
      setData([...data, newItem]);
      toast.success('Data ditambahkan');
    }
    setIsModalOpen(false);
  };
  const confirmDelete = () => {
    if (deletingItem) {
      setData(data.filter(item => item[idKey] !== deletingItem[idKey]));
      toast.success('Data dihapus');
    }
    setIsDeleteOpen(false);
  };
  return <div className="space-y-4" data-id="element-1889">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-black/20 p-3 rounded-lg border border-border/30" data-id="element-1890">
        <div className="text-xs font-mono text-muted-foreground" data-id="element-1891">
          Menampilkan {filteredData.length} {title.toLowerCase()}
        </div>
        <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto" data-id="element-1892">
          <Button variant={showFilters ? 'default' : 'outline'} size="sm" onClick={() => setShowFilters(!showFilters)} className={`h-8 border-border/50 font-mono text-xs ${showFilters ? 'bg-primary text-primary-foreground' : 'bg-background/50'}`} data-id="element-1893">
            <Filter className="w-4 h-4 mr-2" data-id="element-1894" />
            Filter
          </Button>
          <div className="flex gap-2 w-full sm:w-auto" data-id="element-1895">
            <Button size="sm" onClick={handleAddClick} className="w-full sm:w-auto text-xs font-mono" data-id="element-1896">
              <Plus className="w-3 h-3 mr-2" data-id="element-1897" /> Tambah
            </Button>
          </div>
        </div>
      </div>

      {showFilters && <FilterBar fields={filterFields} values={filterValues} onChange={handleFilterChange} onApply={() => {}} onReset={handleResetFilters} resultCount={filteredData.length} exportData={filteredData} exportColumns={columns} exportFilename={title.replace(/\s+/g, '_')} data-id="element-1898" />}

      {/* Table / Empty State */}
      {filteredData.length === 0 ? <EmptyState icon={DatabaseZap} title="DATA TIDAK DITEMUKAN" description={`Tidak ada data ${title.toLowerCase()} yang cocok dengan pencarian Anda.`} actionLabel="Bersihkan Pencarian" onAction={handleResetFilters} data-id="element-1899" /> : <div className="border border-border/30 rounded-lg overflow-hidden" data-id="element-1900">
          <Table data-id="element-1901">
            <TableHeader className="bg-black/40" data-id="element-1902">
              <TableRow className="border-border/30 hover:bg-transparent" data-id="element-1903">
                {columns.map(col => <TableHead key={col.key} className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-1904">
                    {col.label}
                  </TableHead>)}
                <TableHead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground text-right" data-id="element-1905">
                  Aksi
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-id="element-1906">
              {filteredData.map((item, idx) => <TableRow key={item[idKey] || idx} className={`border-border/20 hover:bg-white/5 transition-colors ${onRowClick ? 'cursor-pointer' : ''}`} onClick={() => onRowClick && onRowClick(item)} data-id="element-1907">
                  {columns.map(col => <TableCell key={col.key} className="text-xs" data-id="element-1908">
                      {col.render ? col.render(item[col.key], item) : item[col.key]}
                    </TableCell>)}
                  <TableCell className="text-right" data-id="element-1909">
                    <div className="flex justify-end gap-1" data-id="element-1910">
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary" onClick={e => handleEditClick(item, e)} data-id="element-1911">
                        <Edit2 className="w-3 h-3" data-id="element-1912" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10" onClick={e => handleDeleteClick(item, e)} data-id="element-1913">
                        <Trash2 className="w-3 h-3" data-id="element-1914" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>)}
            </TableBody>
          </Table>
        </div>}

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen} data-id="element-1915">
        <DialogContent className="glass-panel-heavy border-border/50 sm:max-w-[425px]" data-id="element-1916">
          <DialogHeader data-id="element-1917">
            <DialogTitle className="font-mono uppercase tracking-widest text-sm" data-id="element-1918">
              {editingItem ? 'Edit Data' : 'Tambah Data'}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4" data-id="element-1919">
            {formFields.map(field => <div key={field.key} className="space-y-2" data-id="element-1920">
                <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-1921">
                  {field.label}
                </label>
                {field.type === 'select' && field.options ? <select value={formData[field.key] || ''} onChange={e => setFormData({
              ...formData,
              [field.key]: e.target.value
            })} className="w-full bg-background/50 border border-border/50 rounded p-2.5 text-xs font-mono focus:outline-none focus:border-primary/50 transition-all" data-id="element-1922">
                    {field.options.map(opt => <option key={opt} value={opt} data-id="element-1923">
                        {opt}
                      </option>)}
                  </select> : <input type={field.type} value={formData[field.key] || ''} onChange={e => setFormData({
              ...formData,
              [field.key]: e.target.value
            })} className="w-full bg-background/50 border border-border/50 rounded p-2.5 text-xs font-mono focus:outline-none focus:border-primary/50 transition-all" data-id="element-1924" />}
              </div>)}
          </div>
          <DialogFooter data-id="element-1925">
            <Button variant="outline" onClick={() => setIsModalOpen(false)} data-id="element-1926">
              Batal
            </Button>
            <Button onClick={handleSave} data-id="element-1927">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Modal */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen} data-id="element-1928">
        <DialogContent className="glass-panel-heavy border-border/50 sm:max-w-[400px]" data-id="element-1929">
          <DialogHeader data-id="element-1930">
            <DialogTitle className="font-mono uppercase tracking-widest text-sm text-destructive flex items-center gap-2" data-id="element-1931">
              <Trash2 className="w-4 h-4" data-id="element-1932" /> Konfirmasi Hapus
            </DialogTitle>
            <DialogDescription className="text-xs font-mono mt-2" data-id="element-1933">
              Apakah Anda yakin ingin menghapus data ini? Tindakan ini tidak
              dapat dibatalkan.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4" data-id="element-1934">
            <Button variant="outline" onClick={() => setIsDeleteOpen(false)} data-id="element-1935">
              Batal
            </Button>
            <Button variant="destructive" onClick={confirmDelete} data-id="element-1936">
              Hapus
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>;
}
// --- Data Derivation & Mapping ---
const KATEGORI_KETERANGAN: Record<string, string> = {
  Politik: 'Dinamika politik & pemerintahan',
  Sosial: 'Isu kemasyarakatan & opini publik',
  Ekonomi: 'Stabilitas ekonomi & moneter',
  Keamanan: 'Gangguan keamanan & ketertiban',
  Siber: 'Ancaman ruang siber & informasi',
  Terorisme: 'Aksi & jaringan terorisme',
  Separatisme: 'Gerakan separatis bersenjata',
  'Konflik Sosial': 'Konflik horizontal antarkelompok',
  'Objek Vital Nasional': 'Pengamanan objek vital nasional',
  'Infrastruktur Strategis': 'Infrastruktur strategis negara',
  Bencana: 'Bencana alam & non-alam',
  'Lintas Negara': 'Ancaman lintas batas negara'
};
const derivedProvinces = BINDA_LIST.map(b => ({
  kode: b.kode.replace('BINDA-', 'PRV-'),
  provinsi: b.provinsi,
  ibuKota: b.provinsi,
  zonaWaktu: b.lng > 120 ? 'WIT' : b.lng > 110 ? 'WITA' : 'WIB',
  jumlahBinda: 1
}));
const derivedCategories = KATEGORI_LAPORAN.map((k, i) => ({
  kode: `KAT-${String(i + 1).padStart(2, '0')}`,
  kategori: k,
  keterangan: KATEGORI_KETERANGAN[k] || '',
  status: 'Aktif'
}));
// --- Main Page Component ---
export default function MasterData() {
  const navigate = useNavigate();
  return <div className="space-y-6 animate-in fade-in duration-500 pb-12" data-id="element-1937">
      <div className="flex justify-between items-end border-b border-border/50 pb-4" data-id="element-1938">
        <div data-id="element-1939">
          <h1 className="text-2xl font-heading font-bold tracking-tight text-foreground" data-id="element-1940">
            DATA MASTER
          </h1>
          <p className="text-muted-foreground font-mono text-xs mt-1 uppercase tracking-widest" data-id="element-1941">
            Tabel Referensi Sistem // Registri Inti
          </p>
        </div>
        <div className="flex gap-3" data-id="element-1942">
          <SyncButton data-id="element-1943" />
        </div>
      </div>

      <Card className="glass-panel-heavy border-border/50 shadow-[0_0_20px_rgba(0,0,0,0.5)]" data-id="element-1944">
        <Tabs defaultValue="provinsi" className="w-full" data-id="element-1945">
          <CardHeader className="border-b border-border/50 bg-card/50" data-id="element-1946">
            <div className="flex items-center gap-2" data-id="element-1947">
              <Database className="w-4 h-4 text-primary" data-id="element-1948" />
              <CardTitle className="text-sm font-mono uppercase tracking-widest" data-id="element-1949">
                Manajemen Data Referensi
              </CardTitle>
            </div>
            <div className="overflow-x-auto custom-scrollbar mt-4 pb-2" data-id="element-1950">
              <TabsList className="bg-background/50 border border-border/50 inline-flex min-w-max" data-id="element-1951">
                <TabsTrigger value="provinsi" className="text-xs font-mono uppercase tracking-wider" data-id="element-1952">
                  Provinsi
                </TabsTrigger>
                <TabsTrigger value="wilayah" className="text-xs font-mono uppercase tracking-wider" data-id="element-1953">
                  Wilayah / BINDA
                </TabsTrigger>
                <TabsTrigger value="kategori" className="text-xs font-mono uppercase tracking-wider" data-id="element-1954">
                  Kategori Laporan
                </TabsTrigger>
                <TabsTrigger value="jenis-laporan" className="text-xs font-mono uppercase tracking-wider" data-id="element-1955">
                  Jenis Laporan
                </TabsTrigger>
                <TabsTrigger value="jenis-peringatan" className="text-xs font-mono uppercase tracking-wider" data-id="element-1956">
                  Jenis Peringatan
                </TabsTrigger>
                <TabsTrigger value="kpi" className="text-xs font-mono uppercase tracking-wider" data-id="element-1957">
                  Pengaturan KPI
                </TabsTrigger>
                <TabsTrigger value="peran" className="text-xs font-mono uppercase tracking-wider" data-id="element-1958">
                  Peran Pengguna
                </TabsTrigger>
                <TabsTrigger value="sistem" className="text-xs font-mono uppercase tracking-wider" data-id="element-1959">
                  Parameter Sistem
                </TabsTrigger>
              </TabsList>
            </div>
          </CardHeader>

          <CardContent className="p-4" data-id="element-1960">
            <TabsContent value="provinsi" className="m-0" data-id="element-1961">
              <CrudSection title="Provinsi" initialData={derivedProvinces} idKey="kode" searchKeys={['kode', 'provinsi', 'zonaWaktu']} columns={[{
              key: 'kode',
              label: 'Kode',
              render: v => <span className="font-mono text-muted-foreground" data-id="element-1963">
                        {v}
                      </span>
            }, {
              key: 'provinsi',
              label: 'Provinsi',
              render: v => <span className="font-medium" data-id="element-1964">{v}</span>
            }, {
              key: 'ibuKota',
              label: 'Ibu Kota'
            }, {
              key: 'zonaWaktu',
              label: 'Zona Waktu',
              render: v => <Badge variant="outline" className="text-[9px] font-mono" data-id="element-1965">
                        {v}
                      </Badge>
            }, {
              key: 'jumlahBinda',
              label: 'Jumlah BINDA',
              render: v => <span className="font-mono" data-id="element-1966">{v}</span>
            }]} formFields={[{
              key: 'kode',
              label: 'Kode',
              type: 'text'
            }, {
              key: 'provinsi',
              label: 'Provinsi',
              type: 'text'
            }, {
              key: 'ibuKota',
              label: 'Ibu Kota',
              type: 'text'
            }, {
              key: 'zonaWaktu',
              label: 'Zona Waktu',
              type: 'select',
              options: ['WIB', 'WITA', 'WIT']
            }, {
              key: 'jumlahBinda',
              label: 'Jumlah BINDA',
              type: 'number'
            }]} data-id="element-1962" />
            </TabsContent>

            <TabsContent value="wilayah" className="m-0" data-id="element-1967">
              <CrudSection title="Wilayah / BINDA" initialData={BINDA_LIST} idKey="kode" searchKeys={['kode', 'binda', 'provinsi', 'kepala']} onRowClick={item => navigate(`/regional/${item.kode}`)} columns={[{
              key: 'kode',
              label: 'Kode',
              render: v => <span className="font-mono text-muted-foreground" data-id="element-1969">
                        {v}
                      </span>
            }, {
              key: 'binda',
              label: 'BINDA',
              render: v => <span className="font-medium" data-id="element-1970">{v}</span>
            }, {
              key: 'provinsi',
              label: 'Provinsi'
            }, {
              key: 'kepala',
              label: 'Kepala'
            }, {
              key: 'personelAktif',
              label: 'Personel',
              render: v => <span className="font-mono" data-id="element-1971">{v}</span>
            }, {
              key: 'kpiWilayah',
              label: 'KPI',
              render: v => <span className="font-mono" data-id="element-1972">{v}</span>
            }, {
              key: 'status',
              label: 'Status',
              render: v => <StatusBadge status={v} data-id="element-1973" />
            }]} formFields={[{
              key: 'kode',
              label: 'Kode',
              type: 'text'
            }, {
              key: 'binda',
              label: 'Nama BINDA',
              type: 'text'
            }, {
              key: 'provinsi',
              label: 'Provinsi',
              type: 'text'
            }, {
              key: 'kepala',
              label: 'Kepala BINDA',
              type: 'text'
            }, {
              key: 'status',
              label: 'Status',
              type: 'select',
              options: ['Normal', 'Perhatian', 'Waspada', 'Kritis']
            }]} data-id="element-1968" />
            </TabsContent>

            <TabsContent value="kategori" className="m-0" data-id="element-1974">
              <CrudSection title="Kategori Laporan" initialData={derivedCategories} idKey="kode" searchKeys={['kode', 'kategori', 'keterangan']} columns={[{
              key: 'kode',
              label: 'Kode',
              render: v => <span className="font-mono text-muted-foreground" data-id="element-1976">
                        {v}
                      </span>
            }, {
              key: 'kategori',
              label: 'Kategori',
              render: v => <span className="font-medium" data-id="element-1977">{v}</span>
            }, {
              key: 'keterangan',
              label: 'Keterangan',
              render: v => <span className="text-muted-foreground" data-id="element-1978">{v}</span>
            }, {
              key: 'status',
              label: 'Status',
              render: v => <Badge variant={v === 'Aktif' ? 'outline' : 'secondary'} className={v === 'Aktif' ? 'border-primary/50 text-primary' : ''} data-id="element-1979">
                        {v}
                      </Badge>
            }]} formFields={[{
              key: 'kode',
              label: 'Kode',
              type: 'text'
            }, {
              key: 'kategori',
              label: 'Kategori',
              type: 'text'
            }, {
              key: 'keterangan',
              label: 'Keterangan',
              type: 'text'
            }, {
              key: 'status',
              label: 'Status',
              type: 'select',
              options: ['Aktif', 'Nonaktif']
            }]} data-id="element-1975" />
            </TabsContent>

            <TabsContent value="jenis-laporan" className="m-0" data-id="element-1980">
              <CrudSection title="Jenis Laporan" initialData={reportTypesList} idKey="id" searchKeys={['id', 'type', 'description']} columns={[{
              key: 'id',
              label: 'Kode',
              render: v => <span className="font-mono text-muted-foreground" data-id="element-1982">
                        {v}
                      </span>
            }, {
              key: 'type',
              label: 'Jenis',
              render: v => <span className="font-medium" data-id="element-1983">{v}</span>
            }, {
              key: 'description',
              label: 'Deskripsi',
              render: v => <span className="text-muted-foreground" data-id="element-1984">{v}</span>
            }, {
              key: 'sla',
              label: 'SLA Verifikasi',
              render: v => <span className="font-mono" data-id="element-1985">{v}</span>
            }, {
              key: 'status',
              label: 'Status',
              render: v => <Badge variant="outline" className="border-primary/50 text-primary" data-id="element-1986">
                        {v}
                      </Badge>
            }]} formFields={[{
              key: 'id',
              label: 'Kode',
              type: 'text'
            }, {
              key: 'type',
              label: 'Jenis Laporan',
              type: 'text'
            }, {
              key: 'description',
              label: 'Deskripsi',
              type: 'text'
            }, {
              key: 'sla',
              label: 'SLA Verifikasi',
              type: 'text'
            }, {
              key: 'status',
              label: 'Status',
              type: 'select',
              options: ['Aktif', 'Nonaktif']
            }]} data-id="element-1981" />
            </TabsContent>

            <TabsContent value="jenis-peringatan" className="m-0" data-id="element-1987">
              <CrudSection title="Jenis Peringatan" initialData={alertTypesList} idKey="id" searchKeys={['id', 'type', 'description']} columns={[{
              key: 'id',
              label: 'Kode',
              render: v => <span className="font-mono text-muted-foreground" data-id="element-1989">
                        {v}
                      </span>
            }, {
              key: 'type',
              label: 'Jenis',
              render: v => <UrgencyBadge urgency={v} data-id="element-1990" />
            }, {
              key: 'description',
              label: 'Deskripsi',
              render: v => <span className="text-muted-foreground" data-id="element-1991">{v}</span>
            }, {
              key: 'autoEscalate',
              label: 'Eskalasi Otomatis',
              render: v => <Badge variant={v === 'Ya' ? 'destructive' : 'outline'} className={v === 'Ya' ? 'bg-destructive/10' : ''} data-id="element-1992">
                        {v}
                      </Badge>
            }, {
              key: 'color',
              label: 'Warna Token',
              render: v => <span className="font-mono text-[10px]" data-id="element-1993">{v}</span>
            }]} formFields={[{
              key: 'id',
              label: 'Kode',
              type: 'text'
            }, {
              key: 'type',
              label: 'Jenis Peringatan',
              type: 'text'
            }, {
              key: 'description',
              label: 'Deskripsi',
              type: 'text'
            }, {
              key: 'autoEscalate',
              label: 'Eskalasi Otomatis',
              type: 'select',
              options: ['Ya', 'Tidak']
            }, {
              key: 'color',
              label: 'Warna Token',
              type: 'text'
            }]} data-id="element-1988" />
            </TabsContent>

            <TabsContent value="kpi" className="m-0" data-id="element-1994">
              <CrudSection title="Pengaturan KPI" initialData={kpiSettingsList} idKey="id" searchKeys={['id', 'metric']} columns={[{
              key: 'id',
              label: 'Kode',
              render: v => <span className="font-mono text-muted-foreground" data-id="element-1996">
                        {v}
                      </span>
            }, {
              key: 'metric',
              label: 'Metrik',
              render: v => <span className="font-medium" data-id="element-1997">{v}</span>
            }, {
              key: 'weight',
              label: 'Bobot (%)',
              render: v => <span className="font-mono text-primary" data-id="element-1998">{v}%</span>
            }, {
              key: 'target',
              label: 'Target',
              render: v => <span className="font-mono text-[var(--status-active)]" data-id="element-1999">
                        {v}
                      </span>
            }, {
              key: 'threshold',
              label: 'Ambang Batas',
              render: v => <span className="font-mono text-[var(--status-standby)]" data-id="element-2000">
                        {v}
                      </span>
            }]} formFields={[{
              key: 'id',
              label: 'Kode',
              type: 'text'
            }, {
              key: 'metric',
              label: 'Metrik KPI',
              type: 'text'
            }, {
              key: 'weight',
              label: 'Bobot (%)',
              type: 'number'
            }, {
              key: 'target',
              label: 'Target',
              type: 'text'
            }, {
              key: 'threshold',
              label: 'Ambang Batas',
              type: 'text'
            }]} data-id="element-1995" />
            </TabsContent>

            <TabsContent value="peran" className="m-0" data-id="element-2001">
              <CrudSection title="Peran Pengguna" initialData={userRolesList} idKey="id" searchKeys={['id', 'role', 'access']} columns={[{
              key: 'id',
              label: 'Kode',
              render: v => <span className="font-mono text-muted-foreground" data-id="element-2003">
                        {v}
                      </span>
            }, {
              key: 'role',
              label: 'Peran',
              render: v => <span className="font-medium" data-id="element-2004">{v}</span>
            }, {
              key: 'clearance',
              label: 'Tingkat Izin',
              render: v => <Badge variant="outline" className="font-mono border-primary/50 text-primary" data-id="element-2005">
                        Level {v}
                      </Badge>
            }, {
              key: 'usersCount',
              label: 'Jumlah Pengguna',
              render: v => <span className="font-mono" data-id="element-2006">{v}</span>
            }, {
              key: 'access',
              label: 'Hak Akses',
              render: v => <span className="text-muted-foreground text-[10px]" data-id="element-2007">
                        {v}
                      </span>
            }]} formFields={[{
              key: 'id',
              label: 'Kode',
              type: 'text'
            }, {
              key: 'role',
              label: 'Nama Peran',
              type: 'text'
            }, {
              key: 'clearance',
              label: 'Tingkat Izin (1-5)',
              type: 'number'
            }, {
              key: 'access',
              label: 'Hak Akses',
              type: 'text'
            }]} data-id="element-2002" />
            </TabsContent>

            <TabsContent value="sistem" className="m-0" data-id="element-2008">
              <CrudSection title="Parameter Sistem" initialData={systemParamsList} idKey="id" searchKeys={['id', 'parameter', 'description']} columns={[{
              key: 'parameter',
              label: 'Parameter',
              render: v => <span className="font-medium" data-id="element-2010">{v}</span>
            }, {
              key: 'value',
              label: 'Nilai',
              render: v => <span className="font-mono text-primary" data-id="element-2011">{v}</span>
            }, {
              key: 'unit',
              label: 'Satuan',
              render: v => <Badge variant="secondary" className="text-[9px] font-mono" data-id="element-2012">
                        {v}
                      </Badge>
            }, {
              key: 'description',
              label: 'Keterangan',
              render: v => <span className="text-muted-foreground" data-id="element-2013">{v}</span>
            }]} formFields={[{
              key: 'parameter',
              label: 'Nama Parameter',
              type: 'text'
            }, {
              key: 'value',
              label: 'Nilai',
              type: 'text'
            }, {
              key: 'unit',
              label: 'Satuan',
              type: 'text'
            }, {
              key: 'description',
              label: 'Keterangan',
              type: 'text'
            }]} data-id="element-2009" />
            </TabsContent>
          </CardContent>
        </Tabs>
      </Card>
    </div>;
}